import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    BugAntIcon,
    MagnifyingGlassIcon,
    KeyIcon,
    BoltIcon,
    CubeIcon,
    ShieldCheckIcon,
    WrenchScrewdriverIcon,
    DocumentTextIcon,
    GlobeAltIcon
} from "@heroicons/react/24/outline";

const CyberSecurity = () => {
    const navigate = useNavigate();

    const handleRequestAssessment = (e) => {
        e?.preventDefault();
        sessionStorage.setItem('demoSource', 'CyberSecurity Page — ThreatAlert');
        navigate('/audit#contact');
    };

    useEffect(() => {
        const els = document.querySelectorAll('.cyber-container .reveal');
        const io = new IntersectionObserver((entries) => {
            entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
        }, { threshold: 0 });
        els.forEach(el => io.observe(el));
        return () => {
            els.forEach(el => io.unobserve(el));
        };
    }, []);

    return (
        <div className="cyber-container">
            <style>{`
        
  .cyber-container {

    --void: #ffffff;
    --panel: #f8fafc;
    --panel-2: #f1f5f9;
    --line: #e2e8f0;
    --line-soft: #f1f5f9;
    --teal: #3b82f6; 
    --teal-dim: #2563eb;
    --purple: #8b5cf6;
    --magenta: #d51776;
    --red: #f43f5e;
    --amber: #f59e0b;
    --text: #0f172a;
    --text-dim: #334155;
    --text-faint: #64748b;
    --grad-brand: linear-gradient(120deg, #3b82f6 0%, #8b5cf6 60%, #d51776 100%);

}
  *{box-sizing:border-box; margin:0; padding:0;}
  html{scroll-behavior:smooth;}
  .cyber-container{
    background:var(--void); color:var(--text); font-family:'Inter', sans-serif;
    line-height:1.5; overflow-x:hidden; -webkit-font-smoothing:antialiased;
  }
  ::selection{ background:var(--teal); color:#04060b; }
  a{ color:inherit; text-decoration:none; }
  .wrap{ max-width:1240px; margin:0 auto; padding:0 32px; }
  h1,h2,h3,h4{ font-family:'Space Grotesk', sans-serif; letter-spacing:-0.01em; }
  .mono{ font-family:'JetBrains Mono', monospace; }
  .eyebrow{
    font-family:'JetBrains Mono', monospace; font-size:12px; letter-spacing:0.14em;
    text-transform:uppercase; color:var(--text-faint); display:flex; align-items:center; gap:10px;
  }
  .eyebrow::before{ content:''; width:18px; height:1px; background:var(--text-faint); display:inline-block; }
  .eyebrow.teal{ color:var(--teal); } .eyebrow.teal::before{ background:var(--teal); }

  /* ---- NAV ---- */
  header{ position:fixed; top:0; left:0; right:0; z-index:100; backdrop-filter:blur(14px);
    background:rgba(4,6,11,0.75); border-bottom:1px solid var(--line-soft); }
  nav.wrap{ display:flex; align-items:center; justify-content:space-between; height:74px; }
  .logo{ display:flex; align-items:center; gap:10px; font-family:'Space Grotesk'; font-weight:700; font-size:18px; }
  .logo .mark{ width:29px; height:29px; border-radius:9px;
    background:conic-gradient(from 200deg, #ff8a3d, #e0369b, #7c5cff, #2ee6b8, #ff8a3d); position:relative; }
  .logo .mark::after{ content:''; position:absolute; inset:6px; border-radius:6px; background:var(--void);
    clip-path:polygon(0 0, 55% 0, 55% 100%, 0 100%); }
  .navlinks{ display:flex; gap:32px; font-size:14px; color:var(--text-dim); }
  .navlinks a:hover{ color:var(--text); }
  .nav-cta{ font-size:13.5px; font-weight:600; padding:10px 20px; border-radius:100px;
    background:var(--teal); color:#04160f; transition:transform .18s; }
  .nav-cta:hover{ transform:translateY(-2px); }
  @media (max-width:880px){ .navlinks{ display:none; } }

  /* ---- HERO ---- */
  .hero{ position:relative; padding:158px 0 60px; overflow:hidden; }
  .hero .eyebrow { color: rgba(255,255,255,0.8); }
  .hero .eyebrow::before { background: rgba(255,255,255,0.8); }
  .hero-top{ position:absolute; left:0; right:0; top:0; height:1px;
    background:linear-gradient(90deg, transparent, rgba(255,255,255,0.2) 15%, rgba(255,255,255,0.2) 85%, transparent); }
  .hero .wrap{ position:relative; }
  .hero-grid{ display:grid; grid-template-columns:1.05fr 0.95fr; gap:56px; align-items:start; }
  .status-pill{ display:inline-flex; align-items:center; gap:8px; padding:6px 12px; border:1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.1);
    border-radius:100px; font-family:'JetBrains Mono'; font-size:11.5px; color:rgba(255,255,255,0.9); margin-bottom:22px; }
  .status-pill .blip{ width:7px; height:7px; border-radius:50%; background:var(--teal); box-shadow:0 0 8px 2px rgba(46,230,184,0.6); animation:blip 1.8s infinite; }
  @keyframes blip{ 0%,100%{opacity:1;} 50%{opacity:.35;} }
  .hero h1{ font-size:clamp(36px,4.6vw,58px); font-weight:700; line-height:1.05; letter-spacing:-0.02em; margin-bottom:22px; color: #ffffff; }
  .hero h1 .g{ background:linear-gradient(90deg,var(--teal),var(--purple)); -webkit-background-clip:text; background-clip:text; color:transparent; }
  .hero p.lede{ font-size:17px; color:rgba(255,255,255,0.8); max-width:480px; margin-bottom:32px; }
  .hero-actions{ display:flex; gap:14px; flex-wrap:wrap; }
  .btn-primary{ background: rgba(143, 4, 174, 1); color:white; font-weight:700; font-size:14.5px; padding:14px 26px;
    border-radius:100px; display:inline-flex; align-items:center; gap:8px;
    box-shadow:0 8px 30px -8px rgba(250, 68, 247, 0.4); transition:transform .18s, box-shadow .18s; }
  .btn-primary:hover{ transform:translateY(-2px); box-shadow:0 12px 34px -6px rgba(46,230,184,0.55); }
  .btn-ghost{ border:1px solid var(--line); color:var(--text); font-weight:600; font-size:14.5px; padding:14px 24px;
    border-radius:100px; transition:border-color .18s, background .18s; }
  .btn-ghost:hover{ border-color:var(--text-dim); background:var(--panel); }

    .btn-primary1{ background: white; color:black; font-weight:700; font-size:14.5px; padding:14px 26px;
    border-radius:100px; display:inline-flex; align-items:center; gap:8px;
    box-shadow:0 8px 30px -8px rgba(241, 150, 206, 0.4); transition:transform .18s, box-shadow .18s; }
  .btn-primary1:hover{ transform:translateY(-2px); box-shadow:0 12px 34px -6px rgba(46,230,184,0.55); }
  .btn-ghost{ border:1px solid var(--line); color:var(--text); font-weight:600; font-size:14.5px; padding:14px 24px;
    border-radius:100px; transition:border-color .18s, background .18s; }
  .btn-ghost:hover{ border-color:var(--text-dim); background:var(--panel); }

  /* hero watchlist panel */
  .watchlist{ border:1px solid var(--line); border-radius:18px; background:var(--panel); overflow:hidden; }
  .wl-head{ display:flex; justify-content:space-between; align-items:center; padding:16px 20px; border-bottom:1px solid var(--line-soft); }
  .wl-head span{ font-family:'JetBrains Mono'; font-size:11px; letter-spacing:0.08em; color:var(--text-faint); text-transform:uppercase; }
  .wl-head .live{ color:var(--teal); display:flex; align-items:center; gap:6px; }
  .wl-head .live::before{ content:''; width:6px; height:6px; border-radius:50%; background:var(--teal); box-shadow:0 0 6px 2px rgba(46,230,184,0.6); }
  .wl-row{ display:flex; align-items:center; gap:12px; padding:12px 20px; border-bottom:1px solid var(--line-soft); font-size:12.6px; }
  .wl-row:last-child{ border-bottom:none; }
  .wl-tag{ font-family:'JetBrains Mono'; font-size:10.5px; padding:3px 8px; border-radius:5px; flex-shrink:0; }
  .wl-tag.r{ background:rgba(255,84,112,0.15); color:var(--red); border:1px solid rgba(255,84,112,0.35); }
  .wl-tag.a{ background:rgba(255,180,84,0.15); color:var(--amber); border:1px solid rgba(255,180,84,0.35); }
  .wl-tag.t{ background:rgba(59,130,246,0.1); color:var(--teal); border:1px solid rgba(46,230,184,0.3); }
  .wl-row .desc{ color:var(--text-dim); flex:1; }
  .wl-row .time{ font-family:'JetBrains Mono'; font-size:11px; color:var(--text-faint); }
  .wl-stats{ display:grid; grid-template-columns:repeat(2,1fr); gap:1px; background:var(--line-soft); }
  .wl-stat{ background:var(--panel-2); padding:20px; }
  .wl-stat .n{ font-family:'Space Grotesk'; font-weight:700; font-size:24px; }
  .wl-stat .n.t{ color:var(--teal);} .wl-stat .n.p{ color:var(--purple);} .wl-stat .n.m{ color:var(--magenta);} .wl-stat .n.a{ color:var(--amber);}
  .wl-stat .l{ font-size:11.5px; color:var(--text-dim); margin-top:3px; }

  /* ---- STAT BAR ---- */
  .statbar{ border-top:1px solid var(--line-soft); border-bottom:1px solid var(--line-soft); background:var(--panel); }
  .statbar .wrap{ display:grid; grid-template-columns:repeat(4,1fr); }
  .stat{ padding:32px 26px; border-right:1px solid var(--line-soft); }
  .stat:last-child{ border-right:none; }
  .stat .num{ font-family:'Space Grotesk'; font-weight:700; font-size:30px; }
  .stat .num.t{ color:var(--teal);} .stat .num.p{ color:var(--purple);} .stat .num.m{ color:var(--magenta);}
  .stat .lbl{ font-size:12.5px; color:var(--text-dim); margin-top:5px; }
  @media (max-width:760px){ .statbar .wrap{ grid-template-columns:1fr 1fr; } }

  section{ padding:112px 0; position:relative; }
  .sec-head{ max-width:640px; margin-bottom:56px; }
  .sec-head h2{ font-size:clamp(27px,3.3vw,40px); font-weight:700; margin-top:16px; line-height:1.14; }
  .sec-head p{ color:var(--text-dim); font-size:15.5px; margin-top:14px; max-width:560px; }

  /* pillars grid */
  #platform{ background:radial-gradient(ellipse 1000px 480px at 85% 0%, rgba(211, 221, 245, 0.07), transparent 60%); }
  .pillar-grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:1px; background:var(--line-soft); border:1px solid var(--line-soft); border-radius:18px; overflow:hidden; }
  .pillar{ background:var(--panel); padding:30px 28px; transition:background .2s; }
  .pillar:hover{ background:var(--panel-2); }
  .pillar .ic{ width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:16px; font-size:17px;
    background:rgba(149, 17, 179, 0.1); border:1px solid rgba(149, 17, 179, 0.1); }
  .pillar h4{ font-size:15.5px; font-weight:600; margin-bottom:8px; }
  .pillar p{ font-size:13.4px; color:var(--text-dim); line-height:1.55; }
  @media (max-width:860px){ .pillar-grid{ grid-template-columns:1fr; } }

  /* value comparison */
  #value{ background:var(--panel); border-top:1px solid var(--line-soft); border-bottom:1px solid var(--line-soft); }
  .value-grid{ display:grid; grid-template-columns:1fr 1fr; gap:56px; align-items:start; }
  @media (max-width:900px){ .value-grid{ grid-template-columns:1fr; } }
  .stack-list{ border:1px solid var(--line); border-radius:16px; overflow:hidden; }
  .stack-row{ display:flex; justify-content:space-between; gap:16px; padding:15px 20px; border-bottom:1px solid var(--line-soft); background:var(--void); font-size:13.5px; }
  .stack-row:last-child{ border-bottom:none; }
  .stack-row .k{ color:var(--text-faint); font-family:'JetBrains Mono'; font-size:11.5px; text-transform:uppercase; letter-spacing:0.06em; flex-shrink:0; padding-top:2px; }
  .stack-row .v{ color:var(--text-dim); text-align:right; }
  .cmp-table{ border:1px solid var(--line); border-radius:16px; overflow:hidden; background:var(--void); }
  .cmp-row{ display:grid; grid-template-columns:1.3fr 1.4fr 0.7fr; gap:10px; padding:14px 20px; border-bottom:1px solid var(--line-soft); align-items:center; font-size:13px; }
  .cmp-row.head{ font-family:'JetBrains Mono'; font-size:10.5px; text-transform:uppercase; letter-spacing:0.07em; color:var(--text-faint); background:var(--panel-2); }
  .cmp-row .name{ font-weight:600; }
  .cmp-row .cap{ color:var(--text-dim); }
  .cmp-row .cost{ color:var(--red); font-family:'JetBrains Mono'; text-align:right; }
  .cmp-total{ display:flex; justify-content:space-between; align-items:center; padding:18px 20px; background:var(--panel-2); }
  .cmp-total .lbl{ font-size:13.5px; font-weight:600; }
  .cmp-total .val{ font-family:'Space Grotesk'; font-weight:700; font-size:22px; color:var(--teal); }
  .savings-badge{ margin-top:14px; padding:16px 20px; border-radius:12px; border:1px solid rgba(235, 16, 255, 0.3); background:rgba(221, 43, 244, 0.06); font-size:13.5px; color:var(--text-dim); }
  .savings-badge b{ color:var(--teal); }

  /* services */
  #services{ background:radial-gradient(ellipse 900px 460px at 15% 0%, rgba(224,54,155,0.07), transparent 60%); }
  .svc-grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:22px; }
  @media (max-width:900px){ .svc-grid{ grid-template-columns:1fr; } }
  .svc-card{ border:1px solid var(--line); border-radius:16px; padding:28px; background:var(--panel); }
  .svc-card .ic{ width:40px; height:40px; border-radius:11px; display:flex; align-items:center; justify-content:center; margin-bottom:18px; font-size:18px;
    background:rgba(149, 17, 179, 0.1); border:1px solid rgba(149, 17, 179, 0.28); }
  .svc-card h4{ font-size:17px; font-weight:600; margin-bottom:10px; }
  .svc-card p{ font-size:13.8px; color:var(--text-dim); line-height:1.6; }

  /* defense grid */
  #defense{ background:var(--panel); border-top:1px solid var(--line-soft); border-bottom:1px solid var(--line-soft); }
  .def-grid{ display:grid; grid-template-columns:1.4fr 1fr 1fr; gap:1px; background:var(--line-soft); border:1px solid var(--line-soft); border-radius:18px; overflow:hidden; }
  .def-card{ background:var(--void); padding:28px; }
  .def-card.feat{ grid-row:span 2; display:flex; flex-direction:column; justify-content:space-between; }
  .def-card .badge{ display:inline-block; font-family:'JetBrains Mono'; font-size:10px; letter-spacing:0.08em; padding:4px 10px; border-radius:100px; border:1px solid var(--line); color:var(--text-faint); margin-bottom:14px; }
  .def-card h4{ font-size:15.5px; font-weight:600; margin-bottom:8px; }
  .def-card.feat h4{ font-size:19px; }
  .def-card p{ font-size:13.3px; color:var(--text-dim); line-height:1.55; }
  @media (max-width:900px){ .def-grid{ grid-template-columns:1fr 1fr; } .def-card.feat{ grid-row:span 1; grid-column:1/-1; } }
  @media (max-width:600px){ .def-grid{ grid-template-columns:1fr; } }

  /* compliance */
  #compliance{ }
  .comp-grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:14px; }
  @media (max-width:860px){ .comp-grid{ grid-template-columns:1fr 1fr; } }
  @media (max-width:560px){ .comp-grid{ grid-template-columns:1fr; } }
  .comp-item{ border:1px solid var(--line); border-radius:12px; padding:20px; background:var(--panel); display:flex; align-items:center; gap:12px; font-size:13.8px; font-weight:600; }
  .comp-item .dot2{ width:8px; height:8px; border-radius:50%; background:var(--grad-brand); flex-shrink:0; }
  .hardening-note{ margin-top:24px; border:1px solid var(--line); border-radius:14px; padding:22px 26px; background:var(--panel); display:flex; gap:16px; align-items:flex-start; }
  .hardening-note .ic{ font-size:20px; flex-shrink:0; }
  .hardening-note p{ font-size:13.6px; color:var(--text-dim); line-height:1.6; }
  .hardening-note strong{ color:var(--text); }

  /* CTA */
  #cta{ text-align:center; padding:126px 0;
    background:radial-gradient(ellipse 700px 400px at 50% 0%, rgba(59,130,246,0.1), transparent 70%); }
  #cta h2{ font-size:clamp(29px,4.2vw,48px); font-weight:700; max-width:740px; margin:20px auto 20px; line-height:1.1; }
  #cta p{ color:var(--text-dim); font-size:16px; max-width:500px; margin:0 auto 34px; }

  footer{ border-top:1px solid var(--line-soft); padding:48px 0 40px; }
  .foot-row{ display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:20px; }
  .foot-row .flinks{ display:flex; gap:24px; font-size:13.3px; color:var(--text-dim); }
  .foot-row .flinks a:hover{ color:var(--text); }
  .foot-small{ font-size:12.3px; color:var(--text-faint); margin-top:24px; }

  .reveal{ opacity:0; transform:translateY(18px); transition:opacity .7s ease, transform .7s ease; }
  .reveal.in{ opacity:1; transform:translateY(0); }
  @media (prefers-reduced-motion: reduce){ .reveal{ opacity:1; transform:none; transition:none; } .status-pill .blip{ animation:none; } }
  a:focus-visible, button:focus-visible{ outline:2px solid var(--teal); outline-offset:3px; border-radius:4px; }

      `}</style>
            <section className="hero relative overflow-hidden z-10 hero-moving-gradient min-h-screen text-white">
                <div className="absolute inset-0 bg-black/25 pointer-events-none" />
                <div className="hero-glow absolute inset-0 pointer-events-none" />
                <div className="absolute top-1/4 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '8s' }} />
                <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '12s' }} />

                <div className="hero-top z-10 relative"></div>
                <div className="wrap hero-grid z-10 relative mt-16 md:mt-0">
                    <div>
                        <div className="status-pill"><span className="blip"></span>ThreatAlert · monitoring 80+ countries, right now</div>
                        <h1>See the breach <span className="g">before it sees you.</span></h1>
                        <p className="lede">Peopleclick's ThreatAlert platform and cyber security team give you the intelligence of a $194K/yr stack — ransomware tracking, attack surface, credential leaks, and 24×7 SOC monitoring — under one roof.</p>
                        <div className="hero-actions">
                            <button onClick={handleRequestAssessment} className="btn-primary1">Request an Assessment →</button>
                            <a href="#platform" className="btn-ghost" style={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff", background: "rgba(255,255,255,0.05)" }}>Explore ThreatAlert</a>
                        </div>
                    </div>

                    <div className="watchlist reveal in">
                        <div className="wl-head"><span>Live Watchlist</span><span className="live mono">STREAMING</span></div>
                        <div className="wl-row"><span className="wl-tag r">RANSOMWARE</span><span className="desc">New victim posted — manufacturing sector</span><span className="time">2m</span></div>
                        <div className="wl-row"><span className="wl-tag a">CREDENTIAL</span><span className="desc">Infostealer log matched watchlist domain</span><span className="time">14m</span></div>
                        <div className="wl-row"><span className="wl-tag t">SURFACE</span><span className="desc">New subdomain + wildcard cert detected</span><span className="time">31m</span></div>
                        <div className="wl-row"><span className="wl-tag r">SUPPLY CHAIN</span><span className="desc">Vendor mentioned in dark web forum</span><span className="time">1h</span></div>
                        <div className="wl-stats">
                            <div className="wl-stat"><div className="n t">27,483</div><div className="l">Ransomware victims tracked</div></div>
                            <div className="wl-stat"><div className="n p">1.4M+</div><div className="l">IOCs in watchlist</div></div>
                            <div className="wl-stat"><div className="n m">2,271</div><div className="l">Malware families (OpenCTI)</div></div>
                            <div className="wl-stat"><div className="n a">80+</div><div className="l">Countries monitored</div></div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="statbar">
                <div className="wrap">
                    <div className="stat"><div className="num t">$194K+</div><div className="lbl">combined replacement value / year</div></div>
                    <div className="stat"><div className="num p">12</div><div className="lbl">cron jobs, 2hr–weekly refresh cadence</div></div>
                    <div className="stat"><div className="num m">24/7/365</div><div className="lbl">SOC monitoring across three continents</div></div>
                    <div className="stat"><div className="num">JWT + MFA</div><div className="lbl">plus Okta SSO, self-hosted or cloud</div></div>
                </div>
            </div>

            {/*  PLATFORM  */}
            <section id="platform">
                <div className="wrap">
                    <div className="sec-head reveal">
                        <div className="eyebrow teal">ThreatAlert — Cyber Intelligence Platform</div>
                        <h2>Six intelligence pillars, watching continuously.</h2>
                        <p>Real-time threat monitoring, attack surface management and ransomware intelligence, correlated against your own watchlist — not a generic feed.</p>
                    </div>
                    <div className="pillar-grid reveal">
                        <div className="pillar"><div className="ic"><BugAntIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Dark Intelligence</h4><p>Live ransomware victim tracking across 27K+ incidents, with sector filtering and real-time Slack/Teams alerts.</p></div>
                        <div className="pillar"><div className="ic"><MagnifyingGlassIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Attack Surface</h4><p>Subdomain enumeration, typosquat detection, exposed storage buckets, wildcard certificates and fake mobile apps.</p></div>
                        <div className="pillar"><div className="ic"><KeyIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Credential Leaks</h4><p>Compromised employee credentials pulled from infostealer malware — real username, password and URL data.</p></div>
                        <div className="pillar"><div className="ic"><BoltIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Attack Paths with AI</h4><p>MITRE ATT&amp;CK-mapped breach chains built from active findings, with AI-generated executive narratives.</p></div>
                        <div className="pillar"><div className="ic"><BugAntIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Malware Intelligence</h4><p>2,271 malware families from OpenCTI, correlated against your IOC watchlist, ransomware victims and threat actors.</p></div>
                        <div className="pillar"><div className="ic"><CubeIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Supply Chain Risk</h4><p>Custom vendor lists monitored for ransomware hits, dark web mentions and IOC correlations.</p></div>
                    </div>
                </div>
            </section>

            {/*  VALUE  */}
            <section id="value">
                <div className="wrap">
                    <div className="sec-head reveal">
                        <div className="eyebrow">Architecture &amp; Business Value</div>
                        <h2>Built lean. Priced to replace four subscriptions, not add one.</h2>
                        <p>ThreatAlert runs on a straightforward, auditable stack — and stitches together the same intelligence categories four separate commercial platforms sell on their own.</p>
                    </div>
                    <div className="value-grid reveal">
                        <div>
                            <h3 style={{ "fontSize": "17px", "marginBottom": "14px" }}>Technical stack</h3>
                            <div className="stack-list">
                                <div className="stack-row"><span className="k">Platform</span><span className="v">Node.js + Express · any cloud · self-hosted</span></div>
                                <div className="stack-row"><span className="k">Database</span><span className="v">SQLite · MongoDB</span></div>
                                <div className="stack-row"><span className="k">Scheduler</span><span className="v">12 cron jobs, 2hr–weekly cadence</span></div>
                                <div className="stack-row"><span className="k">Alerts</span><span className="v">Slack / Teams webhooks — findings, ransomware, credentials</span></div>
                                <div className="stack-row"><span className="k">Auth</span><span className="v">JWT + MFA + Okta SSO support</span></div>
                                <div className="stack-row"><span className="k">Feeds</span><span className="v">OTX · ThreatFox · URLhaus · Feodo · CISA + more</span></div>
                            </div>
                        </div>
                        <div>
                            <h3 style={{ "fontSize": "17px", "marginBottom": "14px" }}>Value vs. commercial tools</h3>
                            <div className="cmp-table">
                                <div className="cmp-row head"><span>Platform</span><span>Capability</span><span style={{ "textAlign": "right" }}>Est. cost</span></div>
                                <div className="cmp-row"><span className="name">SOCRadar</span><span className="cap">Attack surface + dark web</span><span className="cost">$75K/yr</span></div>
                                <div className="cmp-row"><span className="name">HudsonRock</span><span className="cap">Credential intelligence</span><span className="cost">$24K/yr</span></div>
                                <div className="cmp-row"><span className="name">Recorded Future</span><span className="cap">Threat intelligence</span><span className="cost">$65K/yr</span></div>
                                <div className="cmp-row" style={{ "borderBottom": "none" }}><span className="name">SpyCloud</span><span className="cap">Breach monitoring</span><span className="cost">$30K/yr</span></div>
                                <div className="cmp-total"><span className="lbl">Combined replacement value</span><span className="val">$194K+/yr</span></div>
                            </div>
                            <div className="savings-badge">ThreatAlert delivers equivalent intelligence at <b>~78% lower licensing cost</b> — self-hosted or cloud-hosted, your choice.</div>
                        </div>
                    </div>
                </div>
            </section>

            {/*  SERVICES  */}
            <section id="services">
                <div className="wrap">
                    <div className="sec-head reveal">
                        <div className="eyebrow">Cyber Security Services</div>
                        <h2>Platform tells you what's happening. Our team decides what to do about it.</h2>
                    </div>
                    <div className="svc-grid reveal">
                        <div className="svc-card"><div className="ic"><ShieldCheckIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Virtual CISO (vCISO)</h4><p>Elite security leadership providing strategic direction, budget planning and program optimization — without a full-time executive hire.</p></div>
                        <div className="svc-card"><div className="ic"><WrenchScrewdriverIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Security Architecture</h4><p>Design and review of resilient network architectures and security stacks, tailored to your specific business and threat model.</p></div>
                        <div className="svc-card"><div className="ic"><DocumentTextIcon className="w-5 h-5 text-primaryBlue" /></div><h4>Policy Development</h4><p>Formalizing your security posture through robust, enforceable policies that meet international standards.</p></div>
                    </div>
                </div>
            </section>

            {/*  DEFENSE  */}
            <section id="defense">
                <div className="wrap">
                    <div className="sec-head reveal">
                        <div className="eyebrow teal">Technical Defense &amp; Monitoring</div>
                        <h2>Zero-gap coverage, three continents deep.</h2>
                    </div>
                    <div className="def-grid reveal">
                        <div className="def-card feat">
                            <div><span className="badge">24 / 7 / 365</span><h4>SOC Monitoring &amp; Response</h4><p>Constant surveillance of your digital perimeter with rapid-action response protocols to contain and neutralize threats in real time. Our global team operates across three continents to ensure zero-gap coverage.</p></div>
                        </div>
                        <div className="def-card"><span className="badge">OFFENSIVE SECURITY</span><h4>Vulnerability &amp; Pen Testing</h4><p>Rigorous offensive testing to uncover and remediate gaps before attackers exploit them.</p></div>
                        <div className="def-card"><span className="badge">PROACTIVE INTELLIGENCE</span><h4>Threat Hunting &amp; Intel</h4><p>Deep-web intelligence and active environment searching for stealthy, persistent threats.</p></div>
                        <div className="def-card"><h4>Microsoft Defender / SIEM</h4><p>Full-stack management of Azure Sentinel and Defender for Endpoint, ensuring maximum ROI.</p></div>
                        <div className="def-card"><h4>Cloud Assessments</h4><p>Securing AWS/Azure/GCP configurations against leakage and unauthorized access.</p></div>
                        <div className="def-card"><h4>Identity &amp; Access (IAM)</h4><p>Strict validation of identity protocols and least-privilege implementation across your entire hybrid-cloud estate.</p></div>
                    </div>
                </div>
            </section>

            {/*  COMPLIANCE  */}
            <section id="compliance">
                <div className="wrap">
                    <div className="sec-head reveal">
                        <div className="eyebrow">Cyber Risk &amp; Compliance</div>
                        <h2>Navigating complex regulatory landscapes with precision and authority.</h2>
                    </div>
                    <div className="comp-grid reveal">
                        <div className="comp-item"><span className="dot2"></span>Firewall &amp; Infrastructure Audit</div>
                        <div className="comp-item"><span className="dot2"></span>ISO 27001 / SOC 2 Support</div>
                        <div className="comp-item"><span className="dot2"></span>Business Risk Assessments</div>
                        <div className="comp-item"><span className="dot2"></span>Third-Party Vendor Risk</div>
                        <div className="comp-item"><span className="dot2"></span>Awareness &amp; Phishing Training</div>
                        <div className="comp-item"><span className="dot2"></span>Ransomware Readiness</div>
                    </div>
                    <div className="hardening-note reveal">
                        <div className="ic"><GlobeAltIcon className="w-6 h-6 text-violet" /></div>
                        <p><strong>Infrastructure Hardening.</strong> These services can be delivered completely remotely, with periodic governance meetings and executive reporting — no on-site footprint required.</p>
                    </div>
                </div>
            </section>

            <section id="cta">
                <div className="wrap">
                    <div className="eyebrow" style={{ "justifyContent": "center" }}>Talk to the team</div>
                    <h2>Find out what's already exposed.</h2>
                    <p>A free scoping call maps your attack surface, checks your domains against 1.4M+ IOCs, and shows you exactly what ThreatAlert would have caught.</p>
                    <button onClick={handleRequestAssessment} className="btn-primary">Request an Assessment →</button>
                </div>
            </section>
        </div>
    );
};

export default CyberSecurity;
